// Andere Spieler als blockige Figuren (Konzept §8: keine Namen, keine Marker).
// Stil-Constraint aus style.js: Palette, kein Detail unter 10 cm.

import { mat, box } from '../render/style.js';

const NEAR_HIDE_M = 2.0; // sonst steckt man im Kopf des anderen
const NEAR_HIDE_M2 = NEAR_HIDE_M * NEAR_HIDE_M;

export class RemotePlayersSystem {
  async init(ctx) {
    this.THREE = ctx.THREE;
    this.root = new ctx.THREE.Group();
    this.root.name = 'remotePlayers';
    ctx.scene.add(this.root);

    this.figures = new Map(); // id -> { group, torso, head, legL, legR, meta }

    this._v0 = new ctx.THREE.Vector3();
    this._v1 = new ctx.THREE.Vector3();
    this._v2 = new ctx.THREE.Vector3();
    this._v3 = new ctx.THREE.Vector3();

    if (ctx.net) {
      ctx.net.on('peerLeft', (id) => this._remove(id));
    }
  }

  update(dt, ctx) {
    const net = ctx.net;
    if (!net) return;

    // Basis-Interpolation (Welt). Für Waggon-Mitfahrer überschreiben wir danach.
    net.interpolate?.(dt);

    const train = ctx.train;
    const selfPos = ctx.playerPos ?? ctx.playerState?.pos;
    const seen = new Set();

    for (const [id, rp] of net.remotePlayers) {
      seen.add(id);
      let fig = this.figures.get(id);
      if (!fig) {
        fig = this._spawn(id);
        this.figures.set(id, fig);
      }

      const pos = this._resolvePos(rp, train, fig.meta);
      const yaw = rp.yaw ?? 0;

      // Nähe: eigene Kamera nicht im Mesh des anderen
      if (selfPos) {
        const dx = pos.x - selfPos.x;
        const dy = pos.y - selfPos.y;
        const dz = pos.z - selfPos.z;
        if (dx * dx + dy * dy + dz * dz < NEAR_HIDE_M2) {
          fig.group.visible = false;
          continue;
        }
      }

      fig.group.visible = true;
      fig.group.position.set(pos.x, pos.y, pos.z);
      fig.group.rotation.y = yaw;
      this._pose(fig, rp.anim ?? 0);
    }

    for (const id of this.figures.keys()) {
      if (!seen.has(id)) this._remove(id);
    }
  }

  /**
   * Auf dem Waggon: Weltpositionen von Snapshots sind bis ~50 ms alt.
   * Bei 50 km/h ist das fast ein Meter — reines Welt-Lerpen schwimmt hinterher.
   * Deshalb in Waggon-Lokalraum interpolieren (gleicher Grund wie der eigene
   * Spieler im Car-Frame, CONTRACT.md).
   *
   * fromLocal = letzte angezeigte Local-Position (stabil über Train-Motion),
   * toLocal   = worldToLocal(snapshot.to) mit aktueller matrixWorld.
   */
  _resolvePos(rp, train, meta) {
    const carId = rp.carId ?? null;

    if (carId && train?.worldToLocal && train?.localToWorld) {
      const toKey = `${rp.to.x.toFixed(2)},${rp.to.y.toFixed(2)},${rp.to.z.toFixed(2)},${carId}`;

      if (meta.toKey !== toKey || meta.carId !== carId) {
        if (meta.carId === carId && meta.hasLocal) {
          meta.fromLocal.x = meta.local.x;
          meta.fromLocal.y = meta.local.y;
          meta.fromLocal.z = meta.local.z;
        } else {
          this._v0.set(rp.from.x, rp.from.y, rp.from.z);
          train.worldToLocal(carId, this._v0, this._v1);
          meta.fromLocal.x = this._v1.x;
          meta.fromLocal.y = this._v1.y;
          meta.fromLocal.z = this._v1.z;
        }
        this._v0.set(rp.to.x, rp.to.y, rp.to.z);
        train.worldToLocal(carId, this._v0, this._v1);
        meta.toLocal.x = this._v1.x;
        meta.toLocal.y = this._v1.y;
        meta.toLocal.z = this._v1.z;
        meta.toKey = toKey;
        meta.carId = carId;
        meta.hasLocal = true;
      }

      // Gleiche Smoothstep wie Connection.interpolate
      const e = rp.t * rp.t * (3 - 2 * rp.t);
      meta.local.x = meta.fromLocal.x + (meta.toLocal.x - meta.fromLocal.x) * e;
      meta.local.y = meta.fromLocal.y + (meta.toLocal.y - meta.fromLocal.y) * e;
      meta.local.z = meta.fromLocal.z + (meta.toLocal.z - meta.fromLocal.z) * e;

      this._v2.set(meta.local.x, meta.local.y, meta.local.z);
      train.localToWorld(carId, this._v2, this._v3);
      return { x: this._v3.x, y: this._v3.y, z: this._v3.z };
    }

    meta.carId = null;
    meta.hasLocal = false;
    return { x: rp.pos.x, y: rp.pos.y, z: rp.pos.z };
  }

  _spawn(id) {
    const THREE = this.THREE;
    const g = new THREE.Group();
    g.name = `peer-${id}`;

    // Maße ≥ 10 cm (STYLE_RULES.minDetailSize)
    const bodyMat = mat('cloth');
    const legMat = mat('denim');
    const headMat = mat('skin');

    const torso = new THREE.Mesh(box(0.42, 0.55, 0.28), bodyMat);
    torso.position.y = 1.05;
    torso.castShadow = true;

    const head = new THREE.Mesh(box(0.28, 0.28, 0.28), headMat);
    head.position.y = 1.48;
    head.castShadow = true;

    const legL = new THREE.Mesh(box(0.16, 0.55, 0.18), legMat);
    legL.position.set(-0.11, 0.40, 0);
    const legR = new THREE.Mesh(box(0.16, 0.55, 0.18), legMat);
    legR.position.set(0.11, 0.40, 0);

    g.add(torso, head, legL, legR);
    this.root.add(g);

    return {
      group: g,
      torso,
      head,
      legL,
      legR,
      meta: {
        carId: null,
        fromLocal: { x: 0, y: 0, z: 0 },
        toLocal: { x: 0, y: 0, z: 0 },
        local: { x: 0, y: 0, z: 0 },
        toKey: '',
        hasLocal: false,
      },
    };
  }

  _pose(fig, anim) {
    // Minimal: Ducken / Hängen andeuten
    const crouch = anim === 3 || anim === 8; // CROUCH / RIDE_SIT
    const hang = anim === 7;
    const targetY = crouch ? 0.85 : hang ? 1.15 : 1.05;
    fig.torso.position.y = targetY;
    fig.head.position.y = targetY + 0.43;
    const legH = crouch ? 0.28 : 0.40;
    fig.legL.position.y = legH;
    fig.legR.position.y = legH;
  }

  _remove(id) {
    const fig = this.figures.get(id);
    if (!fig) return;
    this.root.remove(fig.group);
    // Geometrien/Materialien sind in style.js gecacht — nicht disposen.
    this.figures.delete(id);
  }

  dispose() {
    for (const id of [...this.figures.keys()]) this._remove(id);
    if (this.root?.parent) this.root.parent.remove(this.root);
  }
}
